import { SCol, SRow, STag } from "@avalara/skylab-sdk/react";
import React from "react";

/**
 * Shared presentation helpers for the read-only ECMS detail dialogs
 * ({@link ./EcmsCustomerDetailDialog} and {@link ./EcmsCertificateDetailDialog}).
 * Layout is fully Skylab, no companion CSS: SRow/SCol handle columns and utility classes the rest.
 */

/** OData zero-date placeholder the API returns for unset dates. */
export const ZERO_DATE = "0001-01-01T00:00:00";

export const EM_DASH = "—";

const DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
});

/** API values arrive as strings or numbers (e.g. numeric businessTypeId / postalCode). */
export type FieldValue = string | number | null | undefined;

/** Coerce any field value to a trimmed string; null/undefined become "". */
export const toText = (value: FieldValue): string =>
    value === null || value === undefined ? "" : String(value).trim();

/** Format an ISO date as "Sep 30, 2026"; blanks and the zero-date placeholder show a fallback. */
export const formatDate = (value: FieldValue, emptyLabel: string): string => {
    const raw = toText(value);
    if (!raw || raw === ZERO_DATE) {
        return emptyLabel;
    }
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? raw : DATE_FORMAT.format(parsed);
};

/** Trimmed value or an em-dash fallback for blanks. */
export const displayValue = (value: FieldValue): string => toText(value) || EM_DASH;

/** Join address lines / code + description into one display string, dropping blanks. */
export const joinParts = (parts: FieldValue[], separator: string): string => {
    const cleaned = parts.map(toText).filter(Boolean);
    return cleaned.length > 0 ? cleaned.join(separator) : EM_DASH;
};

/** Skylab STag semantic colors we map ECMS statuses onto. */
export type TagColor = "positive" | "error" | "neutral";

// ECMS certificates surface only Active / Revoked today; anything else stays neutral grey.
export const statusTagColor = (value: FieldValue): TagColor => {
    const token = toText(value).toLowerCase();
    if (token === "revoked") return "error";
    if (token === "active") return "positive";
    return "neutral";
};

/** Certificate status rendered as a Skylab STag badge in its semantic color. */
export const StatusTag: React.FC<{ value: FieldValue }> = ({ value }) => {
    const text = toText(value);
    if (!text) {
        return <>{EM_DASH}</>;
    }
    return (
        <STag color={statusTagColor(value)}>
            <span slot="content">{text}</span>
        </STag>
    );
};

/** A single stat in a summary card (one third-width column): muted label, value below. */
export const StatItem: React.FC<{ label: string; children: React.ReactNode }> = ({
    label,
    children,
}) => (
    <SCol span="4">
        <div className="flex flex-dir-col">
            <span className="text-xs-strong text-uppercase text-muted">{label}</span>
            <span className="flex align-items-center text-lg font-semibold margin-top-xs">
                {children}
            </span>
        </div>
    </SCol>
);

/** A label/value pair (one half-width column): muted label above, value below. */
export const DetailPair: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <SCol span="6">
        <div className="flex flex-dir-col">
            <span className="text-sm text-muted font-semibold">{label}</span>
            <span>{value}</span>
        </div>
    </SCol>
);

/** A titled section whose label/value pairs lay out in a 2-up SRow; divider between sections. */
export const DetailSection: React.FC<{
    title: string;
    withDivider?: boolean;
    children: React.ReactNode;
}> = ({ title, withDivider = false, children }) => (
    <section>
        {withDivider ? <hr /> : null}
        <h3 className="text-md font-semibold margin-top-none margin-bottom-sm">{title}</h3>
        <SRow>{children}</SRow>
    </section>
);
